import React, { useState, useEffect, useCallback } from "react"
import { observer, inject } from "mobx-react"
import { useTranslate, useNotify } from "react-admin"
import { makeStyles } from "@material-ui/core/styles"
import Table from "@material-ui/core/Table"
import TableHead from "@material-ui/core/TableHead"
import TableBody from "@material-ui/core/TableBody"
import TableRow from "@material-ui/core/TableRow"
import TableCell from "@material-ui/core/TableCell"
import TablePagination from "@material-ui/core/TablePagination"
import TableContainer from "@material-ui/core/TableContainer"
import Paper from "@material-ui/core/Paper"
import Grid from "@material-ui/core/Grid"
import TextField from "@material-ui/core/TextField"
import MenuItem from "@material-ui/core/MenuItem"
import Button from "@material-ui/core/Button"
import Chip from "@material-ui/core/Chip"
import CircularProgress from "@material-ui/core/CircularProgress"
import Tooltip from "@material-ui/core/Tooltip"
import { requests } from "../../fetch/requests"

const useStyles = makeStyles(theme => ({
  container: {
    margin: "60px 24px 24px",
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
  field: {
    minWidth: 150,
  },
  cellError: {
    color: "#c62828",
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  url: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}))

// datetime-local value (local time) -> epoch ms
const toEpochMs = value => (value ? new Date(value).getTime() : undefined)

// Format a Date as a datetime-local input value (YYYY-MM-DDTHH:mm) in local time.
const toLocalInput = date => {
  const pad = n => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Default filter window: the last 7 days up to now.
const defaultDateRange = () => {
  const now = new Date()
  const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  return { from: toLocalInput(from), to: toLocalInput(now) }
}

const AuditLogList = ({ userStore }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const notify = useNotify()
  const t = key => translate(`customRoot.audit_log.${key}`)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [retention, setRetention] = useState(14)

  // Applied filters (what the query actually uses); `form` holds pending edits.
  // Default to the last 7 days up to now.
  const [filters, setFilters] = useState(() => defaultDateRange())
  const [form, setForm] = useState(() => ({ direction: "", source: "", ok: "", q: "", ...defaultDateRange() }))

  const buildQuery = useCallback(() => {
    const query = { skip: page * rowsPerPage, limit: rowsPerPage }
    if (filters.direction) query.direction = filters.direction
    if (filters.source) query.source = filters.source
    if (filters.ok !== "" && filters.ok !== undefined) query.ok = filters.ok
    if (filters.q) query.q = filters.q
    const from = toEpochMs(filters.from)
    const to = toEpochMs(filters.to)
    if (from !== undefined) query.from = from
    if (to !== undefined) query.to = to
    return query
  }, [page, rowsPerPage, filters])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await requests.getAuditLog(userStore, buildQuery())
      setRows(res.data || [])
      setTotal(res.total || 0)
    } catch (err) {
      notify("customRoot.general.error", "warning")
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [userStore, buildQuery, notify])

  useEffect(() => { load() }, [load])

  // Load the configured retention period once.
  useEffect(() => {
    requests.getAuditRetention(userStore)
      .then(res => { if (res && res.days) setRetention(res.days) })
      .catch(() => {})
  }, [userStore])

  const applyFilters = () => {
    setPage(0)
    setFilters({
      direction: form.direction,
      source: form.source,
      ok: form.ok === "" ? "" : form.ok === "true",
      q: form.q.trim(),
      from: form.from,
      to: form.to,
    })
  }

  const clearFilters = () => {
    setPage(0)
    const range = defaultDateRange()
    setForm({ direction: "", source: "", ok: "", q: "", ...range })
    setFilters({ ...range })
  }

  const onFormChange = field => e => setForm({ ...form, [field]: e.target.value })

  const onRetentionChange = async e => {
    const days = e.target.value
    try {
      await requests.setAuditRetention(userStore, days)
      setRetention(days)
      notify("customRoot.audit_log.retention_saved", "success")
    } catch (err) {
      notify("customRoot.general.error", "warning")
    }
  }

  const onClear = async () => {
    if (!window.confirm(t("clear_confirm"))) return
    try {
      await requests.clearAuditLog(userStore)
      notify("customRoot.audit_log.cleared", "success")
      load()
    } catch (err) {
      notify("customRoot.general.error", "warning")
    }
  }

  const renderResult = row => {
    const ok = row.data && row.data.ok
    const status = row.data && row.data.status
    return (
      <Chip
        size="small"
        label={status ? `${status}` : ok ? t("success") : t("error")}
        style={{ backgroundColor: ok ? "#e6f4ea" : "#fdecea", color: ok ? "#1e7e34" : "#c62828" }}
      />
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2} className={classes.filters} alignItems="flex-end">
        <Grid item>
          <TextField
            className={classes.field} select label={t("direction")}
            value={form.direction} onChange={onFormChange("direction")}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t("all")}</MenuItem>
            <MenuItem value="inbound">{t("inbound")}</MenuItem>
            <MenuItem value="outbound">{t("outbound")}</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <TextField
            className={classes.field} select label={t("source")}
            value={form.source} onChange={onFormChange("source")}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t("all")}</MenuItem>
            <MenuItem value="messaging">{t("messaging")}</MenuItem>
            <MenuItem value="login_failure">{t("login_failure")}</MenuItem>
            <MenuItem value="pos">{t("pos")}</MenuItem>
            <MenuItem value="budget">{t("budget")}</MenuItem>
            <MenuItem value="user">{t("user")}</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <TextField
            className={classes.field} select label={t("result")}
            value={form.ok} onChange={onFormChange("ok")}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">{t("all")}</MenuItem>
            <MenuItem value="true">{t("success")}</MenuItem>
            <MenuItem value="false">{t("error")}</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <TextField
            className={classes.field} label={t("search")} value={form.q}
            onChange={onFormChange("q")} placeholder={t("search_placeholder")}
          />
        </Grid>
        <Grid item>
          <TextField
            label={t("from")} type="datetime-local" value={form.from}
            onChange={onFormChange("from")} InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item>
          <TextField
            label={t("to")} type="datetime-local" value={form.to}
            onChange={onFormChange("to")} InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={applyFilters}>{t("apply")}</Button>
        </Grid>
        <Grid item>
          <Button onClick={clearFilters}>{t("clear")}</Button>
        </Grid>
        <Grid item>
          <TextField
            className={classes.field} select label={t("retention")}
            value={retention} onChange={onRetentionChange}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value={1}>{t("retention_1")}</MenuItem>
            <MenuItem value={2}>{t("retention_2")}</MenuItem>
            <MenuItem value={5}>{t("retention_5")}</MenuItem>
            <MenuItem value={7}>{t("retention_7")}</MenuItem>
            <MenuItem value={14}>{t("retention_14")}</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <Button variant="outlined" style={{ color: "#c62828", borderColor: "#c62828" }} onClick={onClear}>{t("clear_log")}</Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        {loading ? (
          <div className={classes.loader}><CircularProgress /></div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("time")}</TableCell>
                <TableCell>{t("direction")}</TableCell>
                <TableCell>{t("source")}</TableCell>
                <TableCell>{t("org")}</TableCell>
                <TableCell>{t("result")}</TableCell>
                <TableCell>{t("method")}</TableCell>
                <TableCell>{t("url")}</TableCell>
                <TableCell>{t("member")}</TableCell>
                <TableCell>{t("ip")}</TableCell>
                <TableCell>{t("error")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center">{t("no_data")}</TableCell></TableRow>
              )}
              {rows.map(row => {
                const d = row.data || {}
                return (
                  <TableRow key={row.id || row._id}>
                    <TableCell>{row.created ? new Date(row.created).toLocaleString("he-IL") : ""}</TableCell>
                    <TableCell>{d.direction ? t(d.direction) : ""}</TableCell>
                    <TableCell>{d.source ? t(d.source) : ""}</TableCell>
                    <TableCell>{d.org || ""}</TableCell>
                    <TableCell>{renderResult(row)}</TableCell>
                    <TableCell>{d.method || ""}</TableCell>
                    <TableCell className={classes.url}><Tooltip title={d.url || ""}><span>{d.url || ""}</span></Tooltip></TableCell>
                    <TableCell>{d.memberId || ""}</TableCell>
                    <TableCell>{d.ip || ""}</TableCell>
                    <TableCell className={classes.cellError}><Tooltip title={d.error || ""}><span>{d.error || ""}</span></Tooltip></TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onChangePage={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </div>
  )
}

export default inject("userStore")(observer(AuditLogList))
